import React from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './PrintWarningModal.css';

interface PrintWarningModalProps {
  isOpen: boolean;
  warnings: string[];
  onRetry: () => void;
  onContinue: () => void;
  onDownloadPdf: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

const PrintWarningModal: React.FC<PrintWarningModalProps> = ({
  isOpen,
  warnings,
  onRetry,
  onContinue,
  onDownloadPdf,
  onClose,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="print-warning-overlay">
      <div className="print-warning-modal">
        <div className="print-warning-header">
          <h2>{t('salesInvoice.printWarningTitle') || 'Print Warnings'}</h2>
          <button
            className="print-warning-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="print-warning-body">
          <p className="print-warning-message">
            {t('salesInvoice.printWarningMessage') || 'The following issues occurred during printing:'}
          </p>

          <ul className="print-warning-list">
            {warnings.map((warning, index) => (
              <li key={index} className="print-warning-item">
                ⚠️ {warning}
              </li>
            ))}
          </ul>
        </div>

        <div className="print-warning-actions">
          <button
            className="ims-btn ims-btn-secondary"
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            {t('common.close') || 'Close'}
          </button>

          <button
            className="ims-btn ims-btn-secondary"
            onClick={onDownloadPdf}
            disabled={isLoading}
            type="button"
            title="Download as PDF instead"
          >
            <Download size={14} strokeWidth={2} style={{ marginRight: 6, display: 'inline' }} />
            {t('salesInvoice.downloadPdf') || 'Download PDF'}
          </button>

          <button
            className="ims-btn ims-btn-success"
            onClick={onRetry}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? <span className="ims-btn-spinner" /> : null}
            {t('common.retry') || 'Retry'}
          </button>

          <button
            className="ims-btn ims-btn-success"
            onClick={onContinue}
            disabled={isLoading}
            type="button"
          >
            {t('common.continue') || 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintWarningModal;
